import { useState } from 'react';
import { supabase } from '../../../lib/supabase';
import type { DiaryEntry } from '../../../lib/supabase';
import { CategoryManager } from '../../../components/feature/CategoryManager';

interface DiaryFormProps {
  userId: string;
  entry?: DiaryEntry | null;
  onSaved: () => void;
  onClose: () => void;
}

export default function DiaryForm({
  userId,
  entry,
  onSaved,
  onClose,
}: DiaryFormProps) {
  const [title, setTitle] = useState(entry?.title || '');
  const [subject, setSubject] = useState(entry?.subject || '');
  const [content, setContent] = useState(entry?.content || '');
  const [studyHours, setStudyHours] = useState<string>(
    entry?.study_hours?.toString() || ''
  );
  const [isPublic, setIsPublic] = useState<boolean>(
    (entry as DiaryEntry & { is_public?: boolean })?.is_public ?? true
  );
  const [tags, setTags] = useState<string[]>(
    (entry as DiaryEntry & { tags?: string[] })?.tags || []
  );
  const [tagInput, setTagInput] = useState('');
  const [mood, setMood] = useState<string>(
    (entry as DiaryEntry & { mood?: string })?.mood || ''
  );
  const [materialUrl, setMaterialUrl] = useState<string>(
    (entry as DiaryEntry & { material_url?: string })?.material_url || ''
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isEdit = !!entry;

  const moods = [
    { value: 'great', emoji: '😊', label: '良い' },
    { value: 'normal', emoji: '😐', label: '普通' },
    { value: 'down', emoji: '😔', label: '落ち込み' },
    { value: 'unmotivated', emoji: '😤', label: '無気力' },
    { value: 'motivated', emoji: '💪', label: 'やる気満々' },
  ];

  const handleAddTag = () => {
    const trimmed = tagInput.trim();
    if (trimmed && !tags.includes(trimmed) && tags.length < 10) {
      setTags([...tags, trimmed]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter((t) => t !== tagToRemove));
  };

  const handleSubmit = async () => {
    // --- validation ---------------------------------------------------------
    if (!title.trim()) {
      setError('タイトルを入力してください');
      return;
    }
    if (!subject) {
      setError('科目を選択してください');
      return;
    }
    if (!content.trim()) {
      setError('内容を入力してください');
      return;
    }
    const hours = parseFloat(studyHours);
    if (isNaN(hours) || hours < 0) {
      setError('学習時間を正しく入力してください');
      return;
    }

    setSaving(true);
    setError('');
    try {
      if (isEdit && entry) {
        const { error: dbErr } = await supabase
          .from('diary_entries')
          .update({
            title: title.trim(),
            subject,
            content: content.trim(),
            study_hours: hours,
            is_public: isPublic,
            tags,
            mood: mood || null,
            material_url: materialUrl.trim() || null,
            updated_at: new Date().toISOString(),
          })
          .eq('id', entry.id)
          .eq('user_id', userId);
        if (dbErr) throw dbErr;
      } else {
        const { error: dbErr } = await supabase
          .from('diary_entries')
          .insert({
            title: title.trim(),
            subject,
            content: content.trim(),
            study_hours: hours,
            is_public: isPublic,
            tags,
            mood: mood || null,
            material_url: materialUrl.trim() || null,
            user_id: userId,
          });
        if (dbErr) throw dbErr;
      }
      onSaved();
    } catch (e) {
      console.error(e);
      setError('保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between z-10">
          <h3 className="text-lg font-bold text-gray-800 dark:text-white">
            {isEdit ? '日記を編集' : '日記を書く'}
          </h3>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl text-gray-600 dark:text-gray-300"></i>
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 text-sm rounded-lg px-4 py-3">
              {error}
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              タイトル <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value.slice(0, 100))}
              placeholder="今日の学習タイトル"
              maxLength={100}
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 bg-white dark:bg-gray-900 dark:text-white"
            />
          </div>

          {/* Subject - CategoryManager */}
          <CategoryManager
            categoryType="subject"
            selectedCategory={subject}
            onSelectCategory={setSubject}
            label="科目"
            placeholder="科目を選択"
          />

          {/* Mood Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              今日の気分（任意）
            </label>
            <div className="grid grid-cols-5 gap-2">
              {moods.map((m) => (
                <button
                  key={m.value}
                  type="button"
                  onClick={() => setMood(mood === m.value ? '' : m.value)}
                  className={`flex flex-col items-center justify-center gap-1 px-2 py-3 rounded-lg border-2 text-xs font-medium transition-all cursor-pointer ${
                    mood === m.value
                      ? 'bg-teal-50 dark:bg-teal-900/30 border-teal-400 dark:border-teal-500 ring-2 ring-teal-300 dark:ring-teal-600'
                      : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                >
                  <span className="text-2xl">{m.emoji}</span>
                  <span className="text-gray-700 dark:text-gray-300">{m.label}</span>
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-1.5">
              気分を記録すると、勉強時間との相関を分析できます
            </p>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              タグ（最大10個）
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value.slice(0, 20))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTag();
                  }
                }}
                placeholder="タグを入力してEnter"
                maxLength={20}
                className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 bg-white dark:bg-gray-900 dark:text-white"
              />
              <button
                type="button"
                onClick={handleAddTag}
                disabled={!tagInput.trim() || tags.length >= 10}
                className="px-4 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                追加
              </button>
            </div>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 text-sm rounded-full"
                  >
                    <i className="ri-price-tag-3-line text-xs"></i>
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="w-4 h-4 flex items-center justify-center hover:bg-teal-200 dark:hover:bg-teal-800 rounded-full transition-colors cursor-pointer"
                    >
                      <i className="ri-close-line text-xs"></i>
                    </button>
                  </span>
                ))}
              </div>
            )}
            <p className="text-xs text-gray-400 mt-1.5">
              タグを追加すると、後で絞り込み検索ができます
            </p>
          </div>

          {/* Study Hours */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              学習時間（時間）<span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={studyHours}
              onChange={(e) => setStudyHours(e.target.value)}
              placeholder="例: 2.5"
              min="0"
              max="24"
              step="0.5"
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 bg-white dark:bg-gray-900 dark:text-white"
            />
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              内容 <span className="text-red-500">*</span>
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="今日学んだことを記録しましょう..."
              rows={10}
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 bg-white dark:bg-gray-900 dark:text-white resize-y"
            />
          </div>

          {/* Public Setting */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              公開設定
            </label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setIsPublic(true)}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  isPublic
                    ? 'bg-teal-50 dark:bg-teal-900/20 border-teal-400 text-teal-700 dark:text-teal-400 ring-2 ring-teal-300 dark:ring-teal-600'
                    : 'bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                <i className="ri-global-line text-base"></i>
                全体公開
              </button>
              <button
                type="button"
                onClick={() => setIsPublic(false)}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  !isPublic
                    ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-400 text-amber-700 dark:text-amber-400 ring-2 ring-amber-300 dark:ring-amber-600'
                    : 'bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                <i className="ri-lock-line text-base"></i>
                自分だけ
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-1.5">
              {isPublic ? '全ユーザーに公開されます' : '自分だけが閲覧できます'}
            </p>
          </div>

          {/* Material URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              教材URL（任意）
            </label>
            <div className="relative">
              <i className="ri-shopping-cart-line absolute left-3 top-1/2 -translate-y-1/2 text-amber-500 dark:text-amber-400"></i>
              <input
                type="url"
                value={materialUrl}
                onChange={(e) => setMaterialUrl(e.target.value)}
                placeholder="https://www.amazon.co.jp/..."
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 dark:focus:ring-amber-400 bg-white dark:bg-gray-900 dark:text-white"
              />
            </div>
            <p className="text-xs text-gray-400 mt-1.5">
              Amazonなどのアフィリエイトリンクをお持ちの方は、ここに貼っていただけます。
              使わなくてもまったく問題ありません。
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              キャンセル
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving}
              className="flex-1 px-4 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              {saving ? '保存中...' : isEdit ? '更新する' : '保存する'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}